import { interpretarNumero } from '@/lib/formato';

import type { ApontamentoDaApi, EnvioDeApontamento, TurnoDaApi } from './api';
import { HORAS_MAXIMAS_DO_TURNO } from './horas';
import { medidorDo, problemaDasLeituras } from './medidor';

/**
 * Estornar e corrigir o turno (ADR-008, 0086) — a parte que não depende de
 * tela, para a mesa e o celular usarem a MESMA regra e o teste a alcançar.
 *
 * Também mora aqui o filtro da folha de turno impressa (só máquina de
 * horímetro, 0086) e os nomes dos turnos cadastrados do dia.
 */

/** O piso do motivo, o mesmo do banco (`org.estorno`, 0046) e da rota. */
export const PISO_DO_MOTIVO = 15;

/** A licença do estorno e da correção do turno. */
export const PERMISSAO_DE_CORRIGIR_TURNO = 'apontamento_hora.estornar';

export function motivoCurto(motivo: string): boolean {
  return motivo.trim().length < PISO_DO_MOTIVO;
}

export const FRASE_DO_MOTIVO_CURTO = `Escreva o motivo com pelo menos ${String(PISO_DO_MOTIVO)} caracteres, dizendo o que aconteceu.`;

/**
 * A FOLHA DE TURNO IMPRESSA é por horímetro: a máquina de odômetro fica fora da
 * escolha dela. O apontamento da mesa e do celular aceita o caminhão desde a
 * decisão 51 (0113); a folha lida por OCR ainda não.
 */
export function maquinasDeHorimetro<T extends { readonly tipoMedidor?: string | null }>(equipamentos: readonly T[]): T[] {
  return equipamentos.filter((e) => e.tipoMedidor !== 'odometro');
}

/** Os nomes dos turnos da empresa no dia da semana de `dia` (aaaa-mm-dd), sem repetir. */
export function nomesDosTurnosDoDia(turnos: readonly TurnoDaApi[], dia: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dia)) return [];
  const diaSemana = new Date(`${dia}T12:00:00`).getDay();
  return [...new Set(turnos.filter((t) => t.diaSemana === diaSemana).map((t) => t.nome))];
}

/** O turno certo, como a correção o digita: tudo texto, como o campo guarda. */
export interface ValoresDaCorrecao {
  readonly dia: string;
  readonly turno: string;
  readonly inicial: string;
  readonly final: string;
  readonly producao: string;
  readonly espera: string;
  readonly deslocamento: string;
  readonly manutencao: string;
  readonly causaId: string;
  readonly motivo: string;
}

/** A correção abre com o turno errado: quem corrige muda só o que está errado. */
export function valoresDoTurno(a: ApontamentoDaApi): ValoresDaCorrecao {
  const campo = (n: string) => String(Number(n)).replace('.', ',');
  return {
    dia: a.dia,
    turno: a.turno,
    inicial: campo(a.horimetroInicial),
    final: campo(a.horimetroFinal),
    producao: campo(a.horasProducao),
    espera: campo(a.horasEspera),
    deslocamento: campo(a.horasDeslocamento),
    manutencao: campo(a.horasManutencao),
    causaId: a.causaId ?? '',
    motivo: '',
  };
}

export type CampoDaCorrecao = 'dia' | 'turno' | 'inicial' | 'final' | 'soma' | 'causa' | 'motivo' | 'igual';

/**
 * Do formulário ao envio, ou a primeira recusa com o campo. As regras do
 * formulário de apontamento (que o servidor cobra de novo), o motivo com o
 * piso do estorno, e a correção que não muda nada — essa não é correção.
 */
export function montarCorrecao(
  original: ApontamentoDaApi,
  v: ValoresDaCorrecao,
): { readonly envio: EnvioDeApontamento; readonly motivo: string } | { readonly campo: CampoDaCorrecao; readonly frase: string } {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.dia)) return { campo: 'dia', frase: 'Informe o dia do turno.' };
  if (v.turno.trim() === '') return { campo: 'turno', frase: 'Diga de que turno é este apontamento.' };
  /* A unidade das leituras é a do medidor do turno (0113): o caminhão em km. */
  const medidor = medidorDo(original.tipoMedidor);
  const inicial = interpretarNumero(v.inicial);
  if (inicial === null || inicial < 0) return { campo: 'inicial', frase: `Informe o ${medidor.noMeio} do início.` };
  const final = interpretarNumero(v.final);
  if (final === null || final < 0) return { campo: 'final', frase: `Informe o ${medidor.noMeio} do fim.` };
  const leituras = problemaDasLeituras(medidor.tipo, inicial, final);
  if (leituras !== null) return { campo: 'final', frase: leituras };
  const hora = (t: string) => (t.trim() === '' ? 0 : interpretarNumero(t));
  const horas = [hora(v.producao), hora(v.espera), hora(v.deslocamento), hora(v.manutencao)];
  if (horas.some((h) => h === null || h < 0 || h > HORAS_MAXIMAS_DO_TURNO)) {
    return { campo: 'soma', frase: 'Cada hora do turno é um número entre 0 e 24.' };
  }
  const [producao = 0, espera = 0, deslocamento = 0, manutencao = 0] = horas as number[];
  if (producao + espera + deslocamento + manutencao > HORAS_MAXIMAS_DO_TURNO) {
    return { campo: 'soma', frase: 'As quatro somam mais de 24 horas — confira a distribuição do turno.' };
  }
  if (v.causaId !== '' && espera + manutencao <= 0) {
    return { campo: 'causa', frase: 'A causa explica hora parada: informe a espera ou a manutenção, ou deixe sem causa.' };
  }
  const envio: EnvioDeApontamento = {
    equipamentoId: original.equipamentoId,
    dia: v.dia,
    turno: v.turno.trim(),
    operadorId: original.operadorId,
    atividade: original.atividade,
    observacao: original.observacao,
    horimetroInicial: inicial,
    horimetroFinal: final,
    horasProducao: producao,
    horasEspera: espera,
    horasDeslocamento: deslocamento,
    horasManutencao: manutencao,
    causaId: v.causaId === '' ? null : v.causaId,
  };
  const igual =
    envio.dia === original.dia &&
    envio.turno.toLocaleUpperCase('pt-BR') === original.turno.toLocaleUpperCase('pt-BR') &&
    inicial === Number(original.horimetroInicial) &&
    final === Number(original.horimetroFinal) &&
    producao === Number(original.horasProducao) &&
    espera === Number(original.horasEspera) &&
    deslocamento === Number(original.horasDeslocamento) &&
    manutencao === Number(original.horasManutencao) &&
    (envio.causaId ?? null) === (original.causaId ?? null);
  if (igual) return { campo: 'igual', frase: 'Nada mudou. Corrija o valor errado, ou estorne o turno se ele não devia existir.' };
  if (motivoCurto(v.motivo)) return { campo: 'motivo', frase: FRASE_DO_MOTIVO_CURTO };
  return { envio, motivo: v.motivo.trim() };
}
